import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IFtthOffer,
  IMobileOffer,
  useOffer,
  useOfferMode,
} from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { Layout, Main, Modal, useOverlay } from "@nju/ui";
import { consentsMachine } from "./consents.machine";
import { ConsentsForm } from "./partials/ConsentsForm";
import { DiscountWarningModal } from "./partials/DiscountWarningModal";

export function ConsentsPage() {
  const mode = useOfferMode();
  const router = useRouter();
  const modal = useOverlay();
  const ftthOrchestrator = useOrchestrators("ftth", { isOptional: true });
  const multiOrderOrchestrator = useOrchestrators("multiOrder", {
    isOptional: true,
  });
  const selectedOrchestrator =
    mode === "ftth" ? ftthOrchestrator : multiOrderOrchestrator;
  const orchestratorId = selectedOrchestrator?.id;
  const offer = useOffer(selectedOrchestrator?.offerId) as
    | IMobileOffer
    | IFtthOffer;

  const [current, send] = useMachine(consentsMachine, {
    context: {
      consentsCatalogue: [],
      orchestratorId,
      mode,
    },
    actions: {
      redirectToNextStep: () => {
        router.push(
          portalRoutes.podsumowanie.get({
            mode,
          }),
        );
      },
      redirectToMyAccount: () => {
        router.push(
          portalRoutes.mojeKonto.index.get({
            mode,
          }),
        );
      },
    },
  });
  const { context, matches } = current;
  const isSaving = matches("submitting") || matches("setConsentsSuccess");

  React.useEffect(() => {
    if (selectedOrchestrator?.hasConsentsSet) {
      router.push(
        portalRoutes.podsumowanie.get({
          mode,
        }),
      );
    }
  }, [router, selectedOrchestrator?.hasConsentsSet, mode]);

  const selectConsent = React.useCallback(
    ({
      value,
      consentId,
      consentVersion,
    }: {
      value: boolean;
      consentId: string;
      consentVersion: number;
    }) => {
      send({
        type: "SELECT_CONSENT",
        consent: {
          consentId,
          consentVersion,
          accepted: value,
        },
      });
    },
    [send],
  );

  const selectAllConsents = (checked: boolean) => {
    send({ type: "SELECT_ALL_CONSENTS", value: checked });
  };

  const handleSubmit = React.useCallback(() => {
    if (
      context.consentsCatalogue.some((c) => {
        return !c.accepted;
      })
    ) {
      modal.open();
      return;
    }
    send({ type: "SUBMIT" });
  }, [send, context.consentsCatalogue, modal]);

  return (
    <Layout>
      <Main center={true}>
        <ConsentsForm
          activationFee={offer?.activationFee || 0}
          context={context}
          handleSubmit={handleSubmit}
          isSaving={isSaving}
          multiOrderItemsCount={multiOrderOrchestrator?.multiOrderItemsCount}
          selectAllConsents={selectAllConsents}
          selectConsent={selectConsent}
        />
        {modal.isOpened && (
          <Modal onClose={modal.close}>
            <DiscountWarningModal
              activationFee={offer?.activationFee || 0}
              activationFeeWithConsentsDiscount={
                offer?.activationFeeWithConsentsDiscount || 0
              }
              isSaving={isSaving}
              multiOrderItemsCount={
                multiOrderOrchestrator?.multiOrderItemsCount
              }
              onClose={modal.close}
              onConfirm={() => {
                send({ type: "SUBMIT" });
              }}
            />
          </Modal>
        )}
      </Main>
    </Layout>
  );
}
